import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import useTheme from '@mui/styles/useTheme';
import useAuth from '../../authentication/useAuth';
import { useUserApi } from '../../apis/hooks/useUsersApi';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import Error from '../../components/Error';
import Success from '../../components/Success';
import FormSubmitOverlay, {
	FORM_SUBMIT_OVERLAY_MODES,
} from '../../components/FormSubmitOverlay';

const DEFAULT_FORM_VALUES = {
	enableShippedNotification: false,
};

function Profile() {
	const { t } = useTranslation('common');
	const theme = useTheme();
	const { authenticatedUser } = useAuth();
	const { getProfile, updateProfile } = useUserApi();
	const isMountedRef = useIsMountedRef();
	const { control, handleSubmit, reset } = useForm({
		defaultValues: DEFAULT_FORM_VALUES,
	});
	const [isSaving, setIsSaving] = React.useState(false);
	const [saveStatus, setSaveStatus] = React.useState('');
	const userId = authenticatedUser?.email;

	// ------------------------------------------------------------------------
	// Keep the form synced with profile data from the API in one simple place.
	// ------------------------------------------------------------------------
	const applyProfileToForm = React.useCallback(
		(profileData) => {
			if (!isMountedRef.current) {
				return;
			}

			reset({
				enableShippedNotification: Boolean(
					profileData?.notificationPreference?.enableShippedNotification
				),
			});
		},
		[isMountedRef, reset]
	);

	// ------------------------------------------------------------
	// Load the user's saved preferences when the page is opened.
	// ------------------------------------------------------------
	const loadProfile = React.useCallback(async () => {
		if (!userId) {
			return;
		}

		const response = await getProfile(userId);

		if (response?.isAborted) {
			return;
		}

		if (response?.isError) {
			console.log(response.error || 'failed to load profile');
			return;
		}

		applyProfileToForm(response?.data);
	}, [applyProfileToForm, getProfile, userId]);

	React.useEffect(() => {
		loadProfile();
	}, [loadProfile]);

	// ------------------------------------------------------------------------
	// Save the latest checkbox choices and refresh the form with server truth.
	// ------------------------------------------------------------------------
	const handleSavePreferences = React.useCallback(
		async (values) => {
			if (!userId) {
				return;
			}

			setSaveStatus('');
			setIsSaving(true);
			const response = await updateProfile(userId, {
				notificationPreference: {
					enableShippedNotification: Boolean(values?.enableShippedNotification),
				},
			});

			if (!response?.isAborted) {
				if (response?.isError) {
					console.log(response.error || 'failed to update profile');
					setSaveStatus('error');
				} else {
					applyProfileToForm(response?.data);
					setSaveStatus('success');
				}
			}

			setIsSaving(false);
		},
		[applyProfileToForm, updateProfile, userId]
	);

	const gotToResetPassword = () => {
		const authUrl = `${
			process.env.REACT_APP_AUTH_URI
		}/Account/ForgotPassword?returnUrl=${encodeURIComponent(
			window.location.origin
		)}`;
		window.location.assign(authUrl);
	};

	return (
		<>
			<Container maxWidth="xl">
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						rowGap: theme.spacing(1),
						marginTop: theme.spacing(2),
					}}
				>
					{saveStatus === 'error' && (
						<Error message={t('account:settings_update_error')} fullWidth />
					)}
					{saveStatus === 'success' && (
						<Success
							message={t('account:settings_updated_success')}
							fullWidth
						/>
					)}
				</Box>
				<Grid
					container
					spacing={0}
					style={{
						marginTop: theme.spacing(4),
						marginBottom: theme.spacing(4),
					}}
				>
					<Grid item xs={12} sx={{ textAlign: 'center' }}>
						<Typography
							variant="caption"
							color="textSecondary"
							style={{ textTransform: 'uppercase' }}
						>
							{t('my_account')}
						</Typography>
					</Grid>
					<Grid
						item
						xs={12}
						sx={{
							textAlign: 'center',
							marginBottom: `${theme.spacing(4)}`,
						}}
					>
						<Typography
							variant="heading1"
							className="customerAccount-fullName"
							style={{ textTransform: 'uppercase' }}
						>
							{authenticatedUser.firstName} {authenticatedUser.lastName}
						</Typography>
					</Grid>
					<Grid
						container
						spacing={3}
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
						}}
					>
						<Grid item xs={12} sm={6} lg={4}>
							<Paper
								elevation={0}
								sx={{
									borderTop: `1px solid ${theme.palette.grey[400]}`,
									paddingTop: theme.spacing(2),
									paddingBottom: theme.spacing(2),
									textAlign: 'left',
									color: theme.palette.text.secondary,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										columnGap: theme.spacing(1),
										flexWrap: 'wrap',
									}}
								>
									<Typography
										variant="caption"
										color="textSecondary"
										style={{ textTransform: 'uppercase' }}
									>
										{t('account:customer_number')}:
									</Typography>
									<Typography
										variant="body"
										color="textSecondary"
										className="customerAccount-customerNumber"
									>
										{authenticatedUser.companyNumber}
									</Typography>
								</Box>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} lg={4}>
							<Paper
								elevation={0}
								sx={{
									borderTop: `1px solid ${theme.palette.grey[400]}`,
									paddingTop: theme.spacing(2),
									paddingBottom: theme.spacing(2),
									textAlign: 'left',
									color: theme.palette.text.secondary,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'baseline',
										columnGap: theme.spacing(1),
										flexWrap: 'wrap',
									}}
								>
									<Typography
										variant="caption"
										color="textSecondary"
										style={{ textTransform: 'uppercase' }}
									>
										{t('company')}:
									</Typography>
									<Typography
										variant="body"
										className="customerAccount-companyName"
									>
										{authenticatedUser.company}
									</Typography>
								</Box>
							</Paper>
						</Grid>
					</Grid>
					<Grid
						container
						spacing={3}
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
						}}
					>
						<Grid item xs={12} sm={6} lg={4}>
							<Paper
								elevation={0}
								sx={{
									borderTop: `1px solid ${theme.palette.grey[400]}`,
									paddingTop: theme.spacing(2),
									paddingBottom: theme.spacing(2),
									textAlign: 'left',
									color: theme.palette.text.secondary,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'baseline',
										columnGap: theme.spacing(1),
										flexWrap: 'wrap',
									}}
								>
									<Typography
										variant="caption"
										style={{ textTransform: 'uppercase' }}
									>
										{t('email')}:
									</Typography>
									<Typography variant="body" className="customerAccount-Email">
										{authenticatedUser.email}
									</Typography>
								</Box>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} lg={4}>
							<Paper
								elevation={0}
								sx={{
									borderTop: `1px solid ${theme.palette.grey[400]}`,
									paddingTop: theme.spacing(2),
									paddingBottom: theme.spacing(2),
									textAlign: 'left',
									color: theme.palette.text.secondary,
								}}
							>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'baseline',
										columnGap: theme.spacing(1),
										flexWrap: 'wrap',
									}}
								>
									<Typography
										variant="caption"
										color="textSecondary"
										style={{ textTransform: 'uppercase' }}
									>
										{t('password')}:
									</Typography>
									<Typography variant="body">**********</Typography>
									<Button
										variant="text"
										color="primary"
										size="small"
										sx={{
											minWidth: 'auto',
											padding: 0,
											marginLeft: theme.spacing(6),
											textTransform: 'none',
										}}
										style={{
											color: theme.palette.primary.main,
											fontWeight: theme.typography.fontWeightBold,
										}}
										role="button"
										data-testid={t('account:resetPassword')}
										aria-label={t('account:resetPassword')}
										onClick={gotToResetPassword}
									>
										{t('account:resetPassword')}
									</Button>
								</Box>
							</Paper>
						</Grid>
					</Grid>
					<Grid
						container
						spacing={0}
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'center',
							marginTop: `${theme.spacing(4)}`,
						}}
					>
						<Grid item xs={12} sm={12} lg={8}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									columnGap: theme.spacing(1),
								}}
							>
								<NotificationsNoneOutlinedIcon color="action" />
								<Typography variant="heading4">Notifications</Typography>
							</Box>
							<Divider
								sx={{
									marginTop: theme.spacing(1),
									marginBottom: theme.spacing(2),
									borderColor: theme.palette.grey[400],
								}}
							/>
							<Typography
								variant="body"
								sx={{
									marginBottom: theme.spacing(1),
									fontWeight: theme.typography.fontWeightBold,
								}}
							>
								Send me email updates when:
							</Typography>
							<FormGroup>
								<Controller
									name="enableShippedNotification"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													color="primary"
													checked={Boolean(field.value)}
													onChange={(event) =>
														field.onChange(event.target.checked)
													}
												/>
											}
											label="My orders are Shipped"
										/>
									)}
								/>
							</FormGroup>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									marginTop: theme.spacing(3),
								}}
							>
								<Button
									variant="contained"
									color="primary"
									size="large"
									type="button"
									sx={{ minWidth: 150 }}
									onClick={handleSubmit(handleSavePreferences)}
									disabled={isSaving}
								>
									{t('save')}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>
			</Container>
			<FormSubmitOverlay
				open={isSaving}
				mode={FORM_SUBMIT_OVERLAY_MODES.PAGE_FORM}
			/>
		</>
	);
}

export default Profile;
